import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getSiteSettings, type SiteSettings } from '@/services/api';

interface SettingsContextType {
  settings: SiteSettings;
  setSettings: (settings: SiteSettings) => void;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>({
    adminMenuVisible: true,
    newsEnabled: true,
    englishEnabled: true
  });
  const [isLoading, setIsLoading] = useState(true);

  const refreshSettings = async () => {
    setIsLoading(true);
    const fetchedSettings = await getSiteSettings();
    setSettings(fetchedSettings);
    setIsLoading(false);
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, setSettings, isLoading, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    // Return default values instead of throwing an error
    console.warn('useSettings is being used outside of SettingsProvider, returning default values');
    return {
      settings: {
        adminMenuVisible: true,
        newsEnabled: true,
        englishEnabled: true
      },
      setSettings: () => {},
      isLoading: false,
      refreshSettings: async () => {}
    };
  }
  return context;
}